/**
 * Разметка сообщений ↔ DOM строки ввода (30.09.2026).
 *
 * Строка ввода мессенджера — contenteditable, который ПОКАЗЫВАЕТ жирный/курсив,
 * а наружу (state `text`, черновики, отправка) отдаёт ту же текстовую разметку,
 * что понимает renderTextWithMentions в ChatPage: **жирный**, *жирный* (бот
 * зарплаты), _курсив_, __подчёркнутый__, ~~зачёркнутый~~, `код`, ||спойлер||.
 * Переносы строк — обычные "\n" в текстовых узлах (white-space: pre-wrap).
 *
 * Здесь три операции:
 *   markupToDom(text)            — разметка → DocumentFragment для поля;
 *   domToMarkup(root, selection) — DOM поля → разметка + позиции выделения
 *                                  в координатах разметки (как selectionStart/End
 *                                  у textarea, маркеры считаются символами);
 *   setDomSelection(root, a, b)  — обратное: позиции разметки → DOM Selection.
 */

const RE = /\*\*([^*\n]+?)\*\*|__([^_\n]+?)__|~~([^~\n]+?)~~|\|\|([^|\n]+?)\|\||`([^`\n]+?)`|\*([^*\n]+?)\*|_([^_\n]+?)_/g;
const WORD = /[\wа-яёА-ЯЁ]/;

type Fmt = "b" | "i" | "u" | "s" | "code" | "spoiler";
const MARK: Record<Fmt, string> = { b: "**", i: "_", u: "__", s: "~~", code: "`", spoiler: "||" };

/** _курсив_ не должен срабатывать внутри snake_case: literal, если с ОБЕИХ сторон буквы. */
export function italicAllowed(text: string, start: number, token: string): boolean {
  const prev = start > 0 ? text[start - 1] : "";
  const next = text[start + token.length] ?? "";
  const body = token.slice(1, -1);
  if (/^\s|\s$/.test(body)) return false;
  return !(WORD.test(prev) && WORD.test(next));
}

function makeEl(fmt: Fmt): HTMLElement {
  if (fmt === "spoiler") {
    const s = document.createElement("span");
    s.className = "cf-spoiler";
    return s;
  }
  return document.createElement(fmt);
}

function fill(parent: Node, text: string, depth: number): void {
  let last = 0;
  RE.lastIndex = 0;
  for (const m of text.matchAll(RE)) {
    const start = m.index ?? 0;
    const token = m[0];
    let fmt: Fmt;
    let inner: string;
    if (token.startsWith("**")) { fmt = "b"; inner = token.slice(2, -2); }
    else if (token.startsWith("__")) { fmt = "u"; inner = token.slice(2, -2); }
    else if (token.startsWith("~~")) { fmt = "s"; inner = token.slice(2, -2); }
    else if (token.startsWith("||")) { fmt = "spoiler"; inner = token.slice(2, -2); }
    else if (token.startsWith("`")) { fmt = "code"; inner = token.slice(1, -1); }
    else if (token.startsWith("*")) { fmt = "b"; inner = token.slice(1, -1); }
    else {
      if (!italicAllowed(text, start, token)) continue;   // остаётся текстом (в хвосте)
      fmt = "i"; inner = token.slice(1, -1);
    }
    if (start > last) parent.appendChild(document.createTextNode(text.slice(last, start)));
    const el = makeEl(fmt);
    if (fmt === "code" || depth >= 2) el.appendChild(document.createTextNode(inner));
    else fill(el, inner, depth + 1);
    parent.appendChild(el);
    last = start + token.length;
  }
  if (last < text.length) parent.appendChild(document.createTextNode(text.slice(last)));
}

export function markupToDom(text: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  fill(frag, text, 0);
  return frag;
}

function fmtOf(el: Element): Fmt | null {
  const tag = el.tagName;
  if (tag === "B" || tag === "STRONG") return "b";
  if (tag === "I" || tag === "EM") return "i";
  if (tag === "U") return "u";
  if (tag === "S" || tag === "STRIKE" || tag === "DEL") return "s";
  if (tag === "CODE") return "code";
  if (el.classList.contains("cf-spoiler")) return "spoiler";
  if (tag === "SPAN") {
    const st = (el as HTMLElement).style;
    if (st.fontWeight === "bold" || Number(st.fontWeight) >= 600) return "b";
    if (st.fontStyle === "italic") return "i";
    if (st.textDecoration.includes("underline")) return "u";
    if (st.textDecoration.includes("line-through")) return "s";
  }
  return null;
}
const BLOCK = new Set(["DIV", "P", "LI", "BLOCKQUOTE", "PRE"]);

export interface Serialized { text: string; selStart: number; selEnd: number }

/** DOM поля → разметка. Позиции selection (anchor/focus) переводятся в смещения
 *  внутри разметки; если выделение не в поле — оба равны длине текста. */
export function domToMarkup(root: HTMLElement, sel: Selection | null): Serialized {
  const out: string[] = [];
  let pos = 0;
  let a = -1, f = -1;
  const anchor = sel && sel.rangeCount > 0 && root.contains(sel.anchorNode) ? { n: sel.anchorNode!, o: sel.anchorOffset } : null;
  const focus = sel && sel.rangeCount > 0 && root.contains(sel.focusNode) ? { n: sel.focusNode!, o: sel.focusOffset } : null;
  const push = (s: string) => { out.push(s); pos += s.length; };
  const mark = (node: Node, off: number) => {
    if (anchor && anchor.n === node && anchor.o === off) a = pos;
    if (focus && focus.n === node && focus.o === off) f = pos;
  };
  const walk = (node: Node, active: Set<Fmt>, inCode: boolean) => {
    if (node.nodeType === 3) {
      const t = node.nodeValue || "";
      if (anchor && anchor.n === node) a = pos + Math.min(anchor.o, t.length);
      if (focus && focus.n === node) f = pos + Math.min(focus.o, t.length);
      push(t);
      return;
    }
    if (node.nodeType !== 1) return;
    const el = node as HTMLElement;
    if (el.tagName === "BR") { mark(el, 0); push("\n"); return; }
    if (el.tagName === "IMG") { push((el as HTMLImageElement).alt || ""); return; }
    const isBlock = node !== root && BLOCK.has(el.tagName);
    if (isBlock && pos > 0 && !out[out.length - 1]?.endsWith("\n")) push("\n");
    const fmt = inCode ? null : fmtOf(el);
    const wrap = fmt !== null && !active.has(fmt) && (el.textContent || "").length > 0;
    if (wrap) push(MARK[fmt!]);
    const next = wrap ? new Set(active).add(fmt!) : active;
    const children = Array.from(el.childNodes);
    for (let k = 0; k < children.length; k++) {
      mark(el, k);
      walk(children[k], next, inCode || fmt === "code");
    }
    mark(el, children.length);
    if (wrap) push(MARK[fmt!]);
  };
  walk(root, new Set(), false);
  let text = out.join("");
  // Chrome оставляет <div><br></div> как пустую строку в конце — не считаем её текстом
  if (text.endsWith("\n") && root.lastChild && (root.lastChild as HTMLElement).tagName === "DIV") {
    const lc = root.lastChild as HTMLElement;
    if (lc.childNodes.length === 1 && (lc.firstChild as HTMLElement).tagName === "BR") text = text.slice(0, -1);
  }
  const len = text.length;
  const s0 = a < 0 ? len : Math.min(a, len);
  const s1 = f < 0 ? len : Math.min(f, len);
  return { text, selStart: Math.min(s0, s1), selEnd: Math.max(s0, s1) };
}

/** Позиции разметки → DOM Selection. Позиция внутри/на краю маркера ставится в
 *  ближайший текст внутри элемента (после открывающего или до закрывающего). */
export function setDomSelection(root: HTMLElement, start: number, end: number): void {
  const sel = window.getSelection();
  if (!sel) return;
  let pos = 0;
  let sNode: Node | null = null, sOff = 0, eNode: Node | null = null, eOff = 0;
  let lastText: Text | null = null;
  const walk = (node: Node, active: Set<Fmt>, inCode: boolean): boolean => {
    if (node.nodeType === 3) {
      const t = node as Text;
      const len = (t.nodeValue || "").length;
      if (sNode === null && start <= pos + len) { sNode = t; sOff = Math.max(0, start - pos); }
      if (eNode === null && end <= pos + len) { eNode = t; eOff = Math.max(0, end - pos); }
      pos += len;
      lastText = t;
      return sNode !== null && eNode !== null;
    }
    if (node.nodeType !== 1) return false;
    const el = node as HTMLElement;
    if (el.tagName === "BR") {
      if (sNode === null && start <= pos) { sNode = el.parentNode!; sOff = Array.from(el.parentNode!.childNodes).indexOf(el); }
      if (eNode === null && end <= pos) { eNode = el.parentNode!; eOff = Array.from(el.parentNode!.childNodes).indexOf(el); }
      pos += 1;
      return sNode !== null && eNode !== null;
    }
    const isBlock = node !== root && BLOCK.has(el.tagName);
    if (isBlock && pos > 0) pos += 1; // "\n" перед блоком (см. domToMarkup)
    const fmt = inCode ? null : fmtOf(el);
    const wrap = fmt !== null && !active.has(fmt) && (el.textContent || "").length > 0;
    if (wrap) pos += MARK[fmt!].length; // позиции внутри маркера → начало содержимого
    const next = wrap ? new Set(active).add(fmt!) : active;
    for (const ch of Array.from(el.childNodes)) {
      if (walk(ch, next, inCode || fmt === "code")) return true;
    }
    if (wrap) pos += MARK[fmt!].length;
    return false;
  };
  walk(root, new Set(), false);
  const range = document.createRange();
  if (sNode === null) {
    if (lastText) { sNode = lastText; sOff = (lastText as Text).length; }
    else { sNode = root; sOff = root.childNodes.length; }
  }
  if (eNode === null) {
    if (lastText) { eNode = lastText; eOff = (lastText as Text).length; }
    else { eNode = root; eOff = root.childNodes.length; }
  }
  try {
    range.setStart(sNode, sOff);
    range.setEnd(eNode, eOff);
    sel.removeAllRanges();
    sel.addRange(range);
  } catch {
    /* повреждённый DOM — оставляем как есть */
  }
}

/** HTML из буфера → текст с переносами между блочными элементами (перенесено из
 *  onPaste textarea: text/plain у telegram-web и amo склеивает абзацы). */
export function htmlToPlain(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const BLK = new Set(["P", "DIV", "BR", "LI", "TR", "UL", "OL", "TABLE", "H1", "H2", "H3", "H4", "H5", "H6", "PRE", "BLOCKQUOTE", "ARTICLE", "SECTION", "HEADER", "FOOTER"]);
  const out: string[] = [];
  const walk = (node: Node): void => {
    if (node.nodeType === 3) { out.push((node.nodeValue || "").replace(/\s+/g, " ")); return; }
    if (node.nodeType !== 1) return;
    const el = node as HTMLElement;
    if (el.tagName === "BR") { out.push("\n"); return; }
    const isBlock = BLK.has(el.tagName);
    if (isBlock && out.length && !out[out.length - 1].endsWith("\n")) out.push("\n");
    el.childNodes.forEach(walk);
    if (isBlock && out.length && !out[out.length - 1].endsWith("\n")) out.push("\n");
  };
  doc.body.childNodes.forEach(walk);
  return out.join("").replace(/\n{3,}/g, "\n\n").replace(/[ \t]+\n/g, "\n").replace(/\n[ \t]+/g, "\n").trim();
}
